import type { EnvVariable } from '../types/envVariables';
import { isEnvironmentApiRowForSubstitution } from './variableScope';

/** Sentinel Environment.id for the local .Env profile (IndexedDB). Not a server row. */
export const LOCAL_DOT_ENV_ID = -2;

export const LOCAL_DOT_ENV_NAME = '.Env (local)';

/** Persisted in request.environment_url when the request uses the local .Env profile. */
export const LOCAL_DOT_ENV_URL_MARKER = '@local.dotenv';

export type EnvVariableScope = 'environment' | 'project';

export type StoredEnvironmentUrlKind = 'local-dot-env' | 'custom';

function normalizeUrlKey(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

export function isLocalDotEnvEnvironmentUrl(url: string | null | undefined): boolean {
  return String(url ?? '').trim() === LOCAL_DOT_ENV_URL_MARKER;
}

export function localDotEnvUrlForSave(): string {
  return LOCAL_DOT_ENV_URL_MARKER;
}

/**
 * Interpret a stored environment_url as .Env, custom URL, or legacy .Env base URL.
 */
export function resolveStoredEnvironmentUrl(
  storedUrl: string | null | undefined,
  localDotEnvUrl: string
): { kind: StoredEnvironmentUrlKind; url: string } | null {
  const trimmed = String(storedUrl ?? '').trim();
  if (!trimmed) return null;
  if (isLocalDotEnvEnvironmentUrl(trimmed)) {
    return { kind: 'local-dot-env', url: String(localDotEnvUrl ?? '').trim() };
  }
  const dotUrl = String(localDotEnvUrl ?? '').trim();
  if (dotUrl && normalizeUrlKey(trimmed) === normalizeUrlKey(dotUrl)) {
    return { kind: 'local-dot-env', url: dotUrl };
  }
  return { kind: 'custom', url: trimmed };
}

/** Resolve a stored environment_url to the live base URL used for HTTP requests. */
export function resolveEffectiveEnvironmentUrl(
  storedUrl: string | null | undefined,
  localDotEnvUrl: string
): string {
  return resolveStoredEnvironmentUrl(storedUrl, localDotEnvUrl)?.url ?? '';
}

/** Restore UI Environment selection from persisted environment_url. */
export function environmentFromStoredUrl(
  storedUrl: string | null | undefined,
  localDotEnvUrl: string
): { id: number; name: string; url: string } | null {
  const resolved = resolveStoredEnvironmentUrl(storedUrl, localDotEnvUrl);
  if (!resolved) return null;
  if (resolved.kind === 'local-dot-env') {
    return createLocalDotEnvEnvironment(resolved.url);
  }
  return { id: -1, name: resolved.url, url: resolved.url };
}

export function isLocalDotEnvId(id: number | null | undefined): boolean {
  return id != null && Number(id) === LOCAL_DOT_ENV_ID;
}

export function isPersistedServerEnvironmentId(id: number | null | undefined): boolean {
  return id != null && Number.isFinite(Number(id)) && Number(id) > 0;
}

export function normalizeEnvVariableScope(scope?: string | null): EnvVariableScope {
  const s = String(scope ?? '').toLowerCase().trim().replace(/_/g, '-');
  return s === 'project' ? 'project' : 'environment';
}

export function envVariableIdentityKey(key: string, scope?: string | null): string {
  return `${normalizeEnvVariableScope(scope)}:${String(key ?? '').trim()}`;
}

function scopePriority(scope?: string | null): number {
  return normalizeEnvVariableScope(scope) === 'environment' ? 2 : 1;
}

/** Same key, environment-scoped value wins over project-scoped. */
export function collapseEnvVarsByScopePriority<T extends { key?: string; scope?: string | null }>(
  vars: T[]
): T[] {
  const map = new Map<string, T>();
  for (const v of vars) {
    const key = String(v?.key ?? '').trim();
    if (!key) continue;
    const existing = map.get(key);
    if (!existing || scopePriority(v.scope) >= scopePriority(existing.scope)) {
      map.set(key, v);
    }
  }
  return Array.from(map.values());
}

export function resolveDotEnvBaseUrl(
  vars: Array<{ key?: string; value?: string }> | null | undefined,
  profileBaseUrl?: string | null
): string {
  const fromProfile = String(profileBaseUrl ?? '').trim();
  if (fromProfile) return fromProfile;
  if (!vars?.length) return '';
  const wanted = new Set(['baseurl', 'base_url']);
  for (const v of vars) {
    const k = String(v?.key ?? '').trim().toLowerCase();
    if (wanted.has(k) && String(v.value ?? '').trim()) {
      return String(v.value).trim();
    }
  }
  return '';
}

export function createLocalDotEnvEnvironment(url = ''): {
  id: number;
  name: string;
  url: string;
} {
  return {
    id: LOCAL_DOT_ENV_ID,
    name: LOCAL_DOT_ENV_NAME,
    url: url.trim(),
  };
}

/** Autocomplete getOptionLabel / input text — URL only; use renderOption for the .Env name. */
export function localDotEnvAutocompleteLabel(option: { url?: string | null }): string {
  const url = String(option.url ?? '').trim();
  return url || LOCAL_DOT_ENV_NAME;
}

export type CollectionEnvOverride = number | { customUrl: string } | null | undefined;

/**
 * Resolve a collection/toolbar environment override to a base URL.
 * Supports server env ids, custom URLs, and the local .Env profile.
 */
export function resolveCollectionEnvOverrideUrl(
  override: CollectionEnvOverride,
  environments: Array<{ id: number; url?: string | null; baseUrl?: string | null }>,
  localDotEnvUrl = ''
): string | null {
  if (override == null) return null;
  if (typeof override === 'number') {
    if (isLocalDotEnvId(override)) {
      const url = String(localDotEnvUrl ?? '').trim();
      return url || null;
    }
    if (override > 0) {
      const env = environments.find((e) => e.id === override);
      const url = String(env?.url || env?.baseUrl || '').trim();
      return url || null;
    }
    return null;
  }
  const custom = String(override.customUrl ?? '').trim();
  return custom || null;
}

/** Floating label for environment Autocomplete when .Env or a server env is selected. */
export function resolveEnvironmentFieldLabel(
  selected: { id?: number | null; name?: string | null } | null | undefined,
  fallback = 'Environment'
): string {
  if (selected == null) return fallback;
  if (isLocalDotEnvId(selected.id)) return LOCAL_DOT_ENV_NAME;
  if (selected.id === -1) return fallback;
  return String(selected.name ?? '').trim() || fallback;
}

export type ServerEnvVarLike = {
  key: string;
  value?: string | null;
  description?: string;
  scope?: string | null;
  collectionId?: number | null;
  requestId?: number | null;
};

/**
 * Overlay used for {{key}} substitution from the chosen execution environment only.
 * Server Environment and .Env are mutually exclusive — they are never merged.
 */
export function resolveExecutionEnvOverlay(args: {
  selectedEnvironmentId: number | null | undefined;
  localDotEnvVars: EnvVariable[];
  serverEnvVars?: ServerEnvVarLike[] | null;
}): EnvVariable[] {
  const id =
    args.selectedEnvironmentId == null || !Number.isFinite(Number(args.selectedEnvironmentId))
      ? null
      : Number(args.selectedEnvironmentId);

  if (isLocalDotEnvId(id)) {
    return collapseEnvVarsByScopePriority(
      (args.localDotEnvVars || []).map((v) => ({
        ...v,
        key: String(v.key ?? '').trim(),
        value: v.value != null ? String(v.value) : '',
        scope: normalizeEnvVariableScope(v.scope),
      }))
    );
  }

  if (isPersistedServerEnvironmentId(id)) {
    const server = (args.serverEnvVars || [])
      .filter(isEnvironmentApiRowForSubstitution)
      .map((v) => ({
        key: String(v.key ?? '').trim(),
        value: v.value != null ? String(v.value) : '',
        description: v.description,
        scope: normalizeEnvVariableScope(v.scope),
        createdAt: '',
        updatedAt: '',
      }))
      .filter((v) => v.key);
    return collapseEnvVarsByScopePriority(server);
  }

  return [];
}

export function prependLocalDotEnvOption<T extends { id: number; name?: string; url?: string }>(
  environments: T[],
  localUrl = ''
): Array<T | { id: number; name: string; url: string }> {
  const local = createLocalDotEnvEnvironment(localUrl);
  const withoutDup = (environments || []).filter((e) => !isLocalDotEnvId(e.id));
  return [local, ...withoutDup];
}
